import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/query-params';
import { resolveEntitiesFromCsvV3 } from '~/stores/use-watchlist/entityResolver';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import {
  normalizeQWSCodelist,
  parseQWSCodelist,
  toResolverCSV,
} from '~/utils/qws-ticker/qwsTickerConverter';

export const QWS_WATCHLIST_NAME = 'QWS-Watchlist';

export type QWSCodelistStatus = 'idle' | 'loading' | 'done' | 'error';

export interface UseQWSCodelistResult {
  status: QWSCodelistStatus;
  watchlistId: string | undefined;
  error: string | undefined;
}

const pickPreferredResolvedTag = (
  primaryTag: string,
  alternativeTags: string[] = []
) => {
  const allTags = [primaryTag, ...alternativeTags].filter(Boolean);
  const nasdTag = allTags.find(tag => tag.endsWith('_nasd'));

  if (nasdTag) {
    return nasdTag;
  }

  const jpxTag = allTags.find(tag => tag.endsWith('_jpx'));

  if (jpxTag) {
    return jpxTag;
  }
  return allTags[0];
};

export const useQWSCodelist = (): UseQWSCodelistResult => {
  const { params, deleteQueryParam } = useQueryParams<Record<string, string>>();
  const codelist = params[queryKeys.codelist];
  const normalizedCodelist = codelist ? normalizeQWSCodelist(codelist) : '';
  const { getLists, createList, updateList, hasListWithName } = useWatchlist(
    state => ({
      getLists: state.getLists,
      createList: state.createList,
      updateList: state.updateList,
      hasListWithName: state.hasListWithName,
    })
  );

  const [flowState, setFlowState] = useState<{
    status: QWSCodelistStatus;
    error: string | undefined;
  }>({ status: 'idle', error: undefined });
  const [watchlistId, setWatchlistId] = useState<string | undefined>();
  const processedCodelistRef = useRef<string | null>(null);

  const runFlow = useCallback(async () => {
    const raw = normalizedCodelist;

    const setErrorState = (errorCode: string, removeCodelist = true) => {
      setFlowState({ status: 'error', error: errorCode });

      if (removeCodelist) {
        deleteQueryParam(queryKeys.codelist, { replace: true });
      }
    };

    if (!raw) {
      setErrorState('empty', false);
      return;
    }

    setFlowState({ status: 'loading', error: undefined });
    setWatchlistId(undefined);

    try {
      const parsed = parseQWSCodelist(raw);
      if (parsed.length === 0) {
        setErrorState('noEntities');
        return;
      }

      const csv = toResolverCSV(parsed);
      const result = await resolveEntitiesFromCsvV3(csv);
      const tags = result.tags
        .map(t => pickPreferredResolvedTag(t.primaryTag, t.alternativeTags))
        .filter(Boolean);

      if (tags.length === 0) {
        setErrorState('noEntities');
        return;
      }

      await getLists();

      if (hasListWithName(QWS_WATCHLIST_NAME)) {
        const lists = useWatchlist.getState().lists;
        const existing = lists.find(l => l.name === QWS_WATCHLIST_NAME)!;
        const res = await updateList({
          id: existing.id,
          name: QWS_WATCHLIST_NAME,
          tags,
        });

        if (res?.error) {
          setErrorState('resolutionFailed');
          return;
        }

        setWatchlistId(existing.id);
      } else {
        const id = await createList(QWS_WATCHLIST_NAME, tags);

        if (id == null) {
          setErrorState('resolutionFailed');
          return;
        }

        setWatchlistId(id);
      }

      deleteQueryParam(queryKeys.codelist, { replace: true });
      setFlowState({ status: 'done', error: undefined });
    } catch {
      setErrorState('resolutionFailed');
    }
  }, [
    normalizedCodelist,
    deleteQueryParam,
    getLists,
    createList,
    updateList,
    hasListWithName,
  ]);

  useEffect(() => {
    if (!normalizedCodelist) {
      processedCodelistRef.current = null;
      setFlowState({ status: 'idle', error: undefined });
      setWatchlistId(undefined);
      return;
    }

    if (
      processedCodelistRef.current === normalizedCodelist ||
      flowState.status === 'loading'
    ) {
      return;
    }

    processedCodelistRef.current = normalizedCodelist;
    runFlow();
  }, [normalizedCodelist, runFlow, flowState.status]);

  return {
    watchlistId,
    status: flowState.status,
    error: flowState.error,
  };
};
